import * as React from 'react';
import { useRef } from 'react';

import { ProductAvailabilityFeedback } from '@components/ProductAvailabilityFeedback';
import { notifyError } from '@utils/notifications';
import {
  availabilityFeedbackProvidedCountKey,
  UNKNOWN_ERROR_MESSAGE,
} from '@constants';
import { getFieldsSingleError } from '@utils/common';
import { ProductAvailabilityFeedbackAnswer } from '@v5Models';
import { useSessionState } from '@utils/storage';

import {
  ProductsAvailabilityFeedbacksDocument,
  useProductsAvailabilityFeedbacksQuery,
  useSubmitProductAvailabilityFeedbackMutation,
} from './graphql.v5.generated';

type OnInitialLoadArg = {
  isEmptyResult: boolean
};

type Props = {
  onInitialLoad?: (arg: OnInitialLoadArg) => void
  compact?: boolean;
  className?: string
};

const FEEDBACKS_TO_SHOW_PER_SESSION = 10;

export const ProductAvailabilityFeedbackContainer: React.FC<Props> = ({
  onInitialLoad,
  compact,
  className,
}) => {
  const [providedCount, setProvidedCount] = useSessionState<number>(
    availabilityFeedbackProvidedCountKey,
    0,
  );

  const isProvided = (providedCount || 0) >= FEEDBACKS_TO_SHOW_PER_SESSION;
  const initialLoadRef = useRef(false);
  const { data, refetch } = useProductsAvailabilityFeedbacksQuery({
    context: {
      v5: true,
      batch: true,
      ignoreAuthError: true,
    },
    onCompleted: (result) => {
      if (!initialLoadRef.current) {
        onInitialLoad?.({ isEmptyResult: !result.productsAvailabilityFeedbacks.length });
      }
      initialLoadRef.current = true;
    },
    fetchPolicy: 'cache-first',
    skip: isProvided,
  });

  const [submitFeedback] = useSubmitProductAvailabilityFeedbackMutation({
    context: {
      v5: true,
    },
    optimisticResponse: () => ({
      submitProductAvailabilityFeedback: {
        errors: [],
      },
    }),
    update: (cache, _, { variables }) => {
      const newFeedbacks = data?.productsAvailabilityFeedbacks.filter(
        (feedback) => feedback.id !== variables!.feedbackId,
      );
      cache.writeQuery({
        query: ProductsAvailabilityFeedbacksDocument,
        data: {
          productsAvailabilityFeedbacks: newFeedbacks,
        },
      });
    },
    onCompleted: (result) => {
      const errors = result.submitProductAvailabilityFeedback.errors;
      if (!errors?.length) {
        setProvidedCount((providedCount || 0) + 1);
        if (data?.productsAvailabilityFeedbacks.length === 0) {
          refetch();
        }
      }
    },
  });

  const handleSubmit = async (feedbackId: string, answer: ProductAvailabilityFeedbackAnswer) => {
    let response;
    try {
      response = await submitFeedback({
        variables: {
          feedbackId,
          answer,
        },
      });
    } catch {
      notifyError(UNKNOWN_ERROR_MESSAGE());
      return null;
    }
    const errors = response.data?.submitProductAvailabilityFeedback.errors;
    if (errors?.length) {
      const errorsMapping = getFieldsSingleError(errors);
      notifyError(Object.values(errorsMapping)[0]);
    }

    return null;
  };

  if (!data?.productsAvailabilityFeedbacks?.length) {
    return null;
  }

  const request = data.productsAvailabilityFeedbacks[0];
  return (
    <ProductAvailabilityFeedback
      className={className}
      compact={compact}
      name={request.product.name}
      imageSrc={request.product.thumbnail}
      onYes={() => handleSubmit(request.id, ProductAvailabilityFeedbackAnswer.Yes)}
      onNo={() => handleSubmit(request.id, ProductAvailabilityFeedbackAnswer.No)}
      onIgnore={() => handleSubmit(request.id, ProductAvailabilityFeedbackAnswer.Ignored)}
    />
  );
};
